/* @jsx h */
import { defineComponent, h, ref, watch } from "vue";
import type { ProductFormData, MenuCreatorProduct } from "../types";
import type { ProductNutritionalInformation } from "@lemmonhq/types";
import { NutritionForm } from "./NutritionForm";
import { useMenu } from "@app/vue/providers/index";

export interface ProductFormProps {
  isOpen: boolean;
  editingProduct: MenuCreatorProduct | null;
  productForm: ProductFormData;
  onSave: () => void;
  onClose: () => void;
  onFormUpdate: (field: keyof ProductFormData, value: string) => void;
}

export const ProductForm = defineComponent<ProductFormProps>({
  name: "ProductForm",
  props: [
    "isOpen",
    "editingProduct",
    "productForm",
    "onSave",
    "onClose",
    "onFormUpdate",
  ],
  setup(props) {
    const { shop } = useMenu();
    const showNutritionSection = ref(false);
    const nutritionData = ref<ProductNutritionalInformation>({
      servingSize: { amount: 100, unit: "g" },
      calories: 0,
      macronutrients: {
        totalFat: { value: 0, unit: "g" },
        totalCarbohydrate: { value: 0, unit: "g" },
        protein: { value: 0, unit: "g" },
      },
      allergens: [],
      dietaryTags: [],
    });

    // Update nutrition data when editing product changes
    watch(
      () => props.editingProduct,
      (product) => {
        if (product?.nutritional_info) {
          nutritionData.value = { ...product.nutritional_info };
          showNutritionSection.value = true;
        } else {
          showNutritionSection.value = false;
          nutritionData.value = {
            servingSize: { amount: 100, unit: "g" },
            calories: 0,
            macronutrients: {
              totalFat: { value: 0, unit: "g" },
              totalCarbohydrate: { value: 0, unit: "g" },
              protein: { value: 0, unit: "g" },
            },
            allergens: [],
            dietaryTags: [],
          };
        }
      },
    );

    const handleNutritionSave = (nutrition: ProductNutritionalInformation) => {
      nutritionData.value = nutrition;
      if (props.editingProduct) {
        props.editingProduct.nutritional_info = nutrition;
      }
    };

    const handleSave = () => {
      if (showNutritionSection.value && props.editingProduct) {
        props.editingProduct.nutritional_info = nutritionData.value;
      }
      props.onSave();
    };

    return () => {
      if (!props.isOpen) return null;

      return (
        <div class="modal modal-open">
          <div class="modal-box max-w-4xl">
            <h3 class="font-serif text-2xl font-light mb-6">
              {props.editingProduct ? "Edit Menu Item" : "Create New Menu Item"}
            </h3>

            <div class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">Item Name</span>
                  </label>
                  <input
                    type="text"
                    value={props.productForm.name}
                    onInput={(e) =>
                      props.onFormUpdate(
                        "name",
                        (e.target as HTMLInputElement).value,
                      )
                    }
                    class="input input-bordered input-lg"
                    placeholder="e.g., Pan-Seared Duck Breast"
                  />
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">
                      Price ({shop.value.currency})
                    </span>
                  </label>
                  <input
                    type="number"
                    step="0.50"
                    value={props.productForm.price}
                    onInput={(e) =>
                      props.onFormUpdate(
                        "price",
                        (e.target as HTMLInputElement).value,
                      )
                    }
                    class="input input-bordered input-lg"
                    placeholder="42.00"
                  />
                </div>
              </div>

              <div class="form-control">
                <label class="label">
                  <span class="label-text font-medium">Description</span>
                </label>
                <textarea
                  value={props.productForm.description}
                  onInput={(e) =>
                    props.onFormUpdate(
                      "description",
                      (e.target as HTMLTextAreaElement).value,
                    )
                  }
                  class="textarea textarea-bordered"
                  rows={4}
                  placeholder="Craft an enticing description of your dish, highlighting key ingredients and preparation methods..."
                />
              </div>

              <div class="grid grid-cols-2 gap-6">
                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">Portion Size</span>
                  </label>
                  <input
                    type="text"
                    value={props.productForm.weight}
                    onInput={(e) =>
                      props.onFormUpdate(
                        "weight",
                        (e.target as HTMLInputElement).value,
                      )
                    }
                    class="input input-bordered"
                    placeholder="250g, 500ml, 6 pieces, etc."
                  />
                </div>

                <div class="form-control">
                  <label class="label">
                    <span class="label-text font-medium">Category</span>
                  </label>
                  <select
                    value={props.productForm.item_type}
                    onChange={(e) =>
                      props.onFormUpdate(
                        "item_type",
                        (e.target as HTMLSelectElement).value,
                      )
                    }
                    class="select select-bordered"
                  >
                    <option value="food">Food</option>
                    <option value="drink">Beverage</option>
                  </select>
                </div>
              </div>

              {/* Nutrition Section Toggle */}
              <div class="border-t pt-6">
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <h4 class="font-semibold text-lg">
                      Nutritional Information
                    </h4>
                    <p class="text-sm text-gray-600">
                      Add comprehensive nutrition data for this item
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      (showNutritionSection.value = !showNutritionSection.value)
                    }
                    class={`btn ${showNutritionSection.value ? "btn-success" : "btn-outline"}`}
                  >
                    <span class="material-symbols-outlined text-sm mr-2">
                      {showNutritionSection.value ? "fact_check" : "add_circle"}
                    </span>
                    {showNutritionSection.value
                      ? "Has Nutrition Data"
                      : "Add Nutrition Data"}
                  </button>
                </div>

                {showNutritionSection.value && (
                  <div class="bg-gray-50 rounded-lg p-6">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div class="form-control">
                        <label class="label">
                          <span class="label-text text-sm">Calories</span>
                        </label>
                        <input
                          type="number"
                          value={nutritionData.value.calories}
                          onInput={(e) =>
                            (nutritionData.value.calories =
                              parseFloat(
                                (e.target as HTMLInputElement).value,
                              ) || 0)
                          }
                          class="input input-bordered input-sm"
                          placeholder="250"
                        />
                      </div>
                      <div class="form-control">
                        <label class="label">
                          <span class="label-text text-sm">Protein (g)</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={
                            nutritionData.value.macronutrients.protein.value
                          }
                          onInput={(e) =>
                            (nutritionData.value.macronutrients.protein.value =
                              parseFloat(
                                (e.target as HTMLInputElement).value,
                              ) || 0)
                          }
                          class="input input-bordered input-sm"
                        />
                      </div>
                      <div class="form-control">
                        <label class="label">
                          <span class="label-text text-sm">Total Fat (g)</span>
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          value={
                            nutritionData.value.macronutrients.totalFat.value
                          }
                          onInput={(e) =>
                            (nutritionData.value.macronutrients.totalFat.value =
                              parseFloat(
                                (e.target as HTMLInputElement).value,
                              ) || 0)
                          }
                          class="input input-bordered input-sm"
                        />
                      </div>
                    </div>

                    <div class="text-center">
                      <NutritionForm
                        isOpen={false}
                        nutritionData={nutritionData.value}
                        onSave={handleNutritionSave}
                        onClose={() => {}}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          // This would open a separate detailed nutrition modal
                          // For now, we'll just show a placeholder
                          alert("Detailed nutrition form would open here");
                        }}
                        class="btn btn-outline btn-sm"
                      >
                        <span class="material-symbols-outlined text-sm mr-2">
                          nutrition
                        </span>
                        Edit Detailed Nutrition
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div class="modal-action mt-8">
              <button onClick={props.onClose} class="btn btn-ghost">
                Cancel
              </button>
              <button
                onClick={handleSave}
                class="btn bg-gray-900 text-white hover:bg-gray-800"
                disabled={
                  !props.productForm.name.trim() || !props.productForm.price
                }
              >
                {props.editingProduct ? "Update" : "Create"} Item
              </button>
            </div>
          </div>
        </div>
      );
    };
  },
});
